import React, { useEffect, useRef, useState } from 'react';

interface BrandSplashLoaderProps {
  embedded?: boolean;
  message?: string;
  className?: string;
}

const BrandSplashLoader: React.FC<BrandSplashLoaderProps> = ({
  embedded = false,
  message,
  className = '',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);
  const [lottieReady, setLottieReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    if (!containerRef.current) return;

    Promise.all([
      import('lottie-web'),
      import('../kithhr_load.json')
    ]).then(([lottieModule, animDataModule]) => {
      if (isCancelled || !containerRef.current) return;
      const lottie = (lottieModule as any).default || lottieModule;
      const kithhrLoadAnimation = (animDataModule as any).default || animDataModule;

      try {
        animRef.current = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: kithhrLoadAnimation,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
          },
        });

        animRef.current.setSpeed(1.6);
        animRef.current.addEventListener('DOMLoaded', () => {
          if (!isCancelled) setLottieReady(true);
        });
        setTimeout(() => {
          if (!isCancelled) setLottieReady(true);
        }, 250);
      } catch (err) {
        console.warn('BrandSplashLoader Lottie error:', err);
        if (!isCancelled) setLottieReady(false);
      }
    }).catch(err => {
      console.warn('Dynamic lottie load error:', err);
      if (!isCancelled) setLottieReady(false);
    });

    return () => {
      isCancelled = true;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, []);

  const content = (
    <div className="flex flex-col items-center justify-center relative select-none">
      {/* Lottie Animation Container */}
      <div
        ref={containerRef}
        className={`w-64 h-64 sm:w-72 sm:h-72 max-w-[85vw] max-h-[45vh] flex items-center justify-center transition-opacity duration-300 ${
          lottieReady ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Instant Fallback Heartbeat & Logo if Lottie is loading or unavailable */}
      {!lottieReady && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none animate-in fade-in duration-200">
          <div className="relative w-24 h-24 mb-4 flex items-center justify-center">
            {/* Glowing Pulse Ring */}
            <div className="absolute inset-0 rounded-3xl bg-teal-500/20 blur-xl animate-pulse" />
            <img
              src="/logo.svg"
              alt="KithHR"
              className="w-20 h-20 rounded-2xl object-contain drop-shadow-[0_8px_24px_rgba(37,202,210,0.45)] relative z-10 animate-bounce"
              style={{ animationDuration: '2s' }}
            />
          </div>
          <p className="text-sm font-black tracking-[0.25em] uppercase text-teal-400/90 drop-shadow-[0_0_12px_rgba(37,202,210,0.6)]">
            KithHR
          </p>
        </div>
      )}

      {/* Subtitle / Message */}
      {message && (
        <p className="mt-4 text-xs sm:text-sm font-semibold tracking-wide text-apple-gray-500 dark:text-apple-gray-400 text-center animate-pulse">
          {message}
        </p>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div
        className={`w-full py-16 flex items-center justify-center ${className}`}
        role="status"
        aria-live="polite"
      >
        {content}
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 dark:bg-black/98 backdrop-blur-xl ${className}`}
      role="status"
      aria-live="polite"
    >
      {content}
    </div>
  );
};

export default BrandSplashLoader;
